import {autorun, observable, runInAction} from 'mobx'
import {StoresInstance} from '@stores'
import {_} from '@api'
import {DragDropStore} from './DragDropStore'

export class WindowDragEventHandlers {
  /*
  // Prevent dragging an image link and not dropping on a drop target causing navigation away from the page
      // This is called repeatedly, we only need to capture the first one
      windowDragEvents.dragOver.addEventListener()
      this._addWindowDropListener()
   */
  /**
   * Only want to capture this once
   */
  dragOver = new Document_DragOver()
  mouseMove = new Document_MouseMove()
  drop = new Document_Drop()

  constructor() {
    DEBUG && window.addEventListener('animationiteration', this.onAnimation, false)
  }

  onAnimation = (e) => {
    if (!_.isEmpty(DragDropStore._dragItem)) {
      console.log(`Animation detected during D&D, this causes huge performance issues!`, e)
    }
  }
}

/**
 * Once we've seen a single global drag over event for the current drag drop interaction we want to stop listening to window events until the drag/drop is complete
 */
export class Document_DragOver {
  constructor() {
    this.addEventListener()
  }

  @observable isListening = false

  /**
   * When the user drags an outside item in like an image or a catalog item from a different webpage, we need to set our dragItem state
   * However we don't want to then trigger this function as the user continues to drag the item around the page.
   * @param e
   */
  onDragOver = (e: DragEvent) => {
    // console.log(`Document_onDragOver`, e.defaultPrevented, e.target, e.currentTarget)

    if (e.button === 0) return

    //
    // Stop the default behavior of the page.  Otherwise normal drop operations won't work and operations like dragging in an image from the outside will cause the browser to open the image in a new page.
    e.preventDefault()// Prevents showing + icon by default and other various things chrome will do if we don't cancel the event
    e.stopPropagation()
    // If we didn't initiate this drag, it came from an outside source such as an image, or a library item dragged from another VIP board running in a separate browser window
    StoresInstance.allStores.forEach(({dragDrop}) => !dragDrop.isDragging && dragDrop.readDataTransfer(e.dataTransfer))

    return false
  }

  addEventListener = () => {
    if (!this.isListening) {
      document.addEventListener('dragover', this.onDragOver, false)
      this.isListening = true
    }
  }

  removeEventListener = () => {
    if (this.isListening) {
      document.removeEventListener('dragover', this.onDragOver, false)
      this.isListening = false
    }
  }
}

export class Document_MouseMove {
  constructor() {
    autorun(() => {
      !_.isEmpty(DragDropStore._dragItem) ? this.addEventListener() : this.removeEventListener()
    }, {name: `Watch for mouse move events when dragging is on in case the user cancels while in another window and comes back to this one`})
  }

  @observable isListening = false

  /**
   * We cannot detect when an outside drag event (from another window) is cancelled
   * If the user moves the mouse but no button is pressed, cancel our drag
   * @param e
   */
  private onMouseMove = (e: MouseEvent) => {
    // console.log(`Document_onMouseMove`)
    if (e.buttons == 0) {
      this.clearDragDropIfNoLongerDragging()
    }
  }

  clearDragDropIfNoLongerDragging = _.throttle(() => {
    if (!_.isEmpty(DragDropStore._dragItem)) {
      DragDropStore._dragItem = null
      StoresInstance.allStores.forEach(({dragDrop}) => dragDrop.reset(true))
    }
  }, 100)

  addEventListener = () => {
    if (!this.isListening) {
      document.addEventListener('mousemove', this.onMouseMove, false)
      this.isListening = true
    }
  }

  removeEventListener = () => {
    if (this.isListening) {
      document.removeEventListener('mousemove', this.onMouseMove, false)
      this.isListening = false
    }
  }
}

export class Document_Drop {
  constructor() {
    this.addEventListener()
  }

  @observable isListening = false
  addEventListener = () => {
    if (!this.isListening) {
      document.addEventListener('drop', this.onDrop, false)
      this.isListening = true
    }
  }

  removeEventListener = () => {
    if (this.isListening) {
      document.removeEventListener('drop', this.onDrop, false)
      this.isListening = false
    }
  }
  /**
   * The user might drop on none of our registered drop targets
   * This will only be called if no one else handled the drop
   * @param e
   */
  private onDrop = (e: DragEvent) => {
    // console.log(`Document_onDrop`)
    e.preventDefault()

    setTimeout(() => {
      runInAction(() => {
        StoresInstance.allStores.forEach(({dragDrop}) => {
          try {
            dragDrop.reset(true)
          } catch (err) {
            console.error(err)
          }
        }) // Will set isDragging to false which will cause us to listen for dragOver again
      })
    }, 100)
  }
}



// WEBPACK FOOTER //
// ./src/stores/dragDrop/WindowDragHandlers.ts